import React from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { format } from "date-fns";
import { getAbsenceTypeColor, getDateLocale, parseDateOnly } from "./teamOverviewUtils";

export const useAbsenceTypeLabel = () => {
  const { t } = useTranslation();
  return (type) => {
    const key = String(type || "").trim().toLowerCase();
    return t(`pages.OrganizationSettings.absence_type_${key}`, { defaultValue: type });
  };
};

const AbsenceBar = ({ absence, placement, lane, personName }) => {
  const { t, i18n } = useTranslation();
  const getTypeLabel = useAbsenceTypeLabel();
  const typeLabel = getTypeLabel(absence.absence_type);
  const locale = getDateLocale(i18n.language);
  const formatDay = (value) => format(parseDateOnly(value), "P", { locale });

  const startLabel = formatDay(absence.start_date);
  const endLabel = formatDay(absence.end_date);
  const dateRange = startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
  const hours =
    absence.start_time && absence.end_time
      ? `${absence.start_time.slice(0, 5)} – ${absence.end_time.slice(0, 5)}`
      : null;
  const statusLabel = t(`components.Calendar.teamOverview.status_${String(absence.status).toLowerCase()}`, {
    defaultValue: absence.status,
  });

  const isHalfDayBlock = absence.is_half_day && placement.span === 1;

  const popover = (
    <Popover id={`absence-${absence.id}`}>
      <Popover.Header as="div" className="fw-bold">
        {personName}
      </Popover.Header>
      <Popover.Body>
        <dl className="team-overview__detail">
          <dt>{t("components.Calendar.teamOverview.type")}</dt>
          <dd>{typeLabel}</dd>
          <dt>{t("components.Calendar.teamOverview.dates")}</dt>
          <dd>
            {dateRange}
            {absence.is_half_day && ` (${t("components.Calendar.teamOverview.half_day")})`}
            {hours && ` · ${hours}`}
          </dd>
          <dt>{t("components.Calendar.teamOverview.status")}</dt>
          <dd>{statusLabel}</dd>
        </dl>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger={["hover", "focus", "click"]} placement="top" overlay={popover} rootClose>
      <button
        type="button"
        className={`team-overview__bar${isHalfDayBlock ? " team-overview__bar--half" : ""}`}
        style={{
          gridColumn: `${placement.startDay + 1} / span ${placement.span}`,
          gridRow: lane + 1,
          backgroundColor: getAbsenceTypeColor(absence.absence_type),
        }}
        aria-label={`${personName}: ${typeLabel}, ${dateRange}, ${statusLabel}`}
      />
    </OverlayTrigger>
  );
};

export default AbsenceBar;
